import { ManagedUser, ManagedUserInput } from "../types/userManagement";

const STORAGE_KEY = "managedUsers";

const nowIso = () => new Date().toISOString();

const readAll = (): ManagedUser[] => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ManagedUser[]) : [];
  } catch {
    return [];
  }
};

const writeAll = (users: ManagedUser[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(users));
};

const ensureSeed = () => {
  const existing = readAll();
  if (existing.length > 0) return;

  const rawCurrentUser = localStorage.getItem("user");
  if (!rawCurrentUser) return;

  try {
    const current = JSON.parse(rawCurrentUser);
    const username = String(current?.username || current?.email || "admin");
    const email = String(current?.email || "admin@example.com");
    const displayName = String(
      current?.displayName || current?.name || username,
    );

    const seeded: ManagedUser = {
      id: crypto?.randomUUID ? crypto.randomUUID() : String(Date.now()),
      username,
      email,
      displayName,
      role: "administrator",
      isActive: true,
      createdAt: nowIso(),
      updatedAt: nowIso(),
      lastLoginAt: nowIso(),
    };

    writeAll([seeded]);
  } catch {
    // ignore
  }
};

export const listManagedUsers = async (): Promise<ManagedUser[]> => {
  ensureSeed();
  const users = readAll();
  return users.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
};

export const createManagedUser = async (
  input: ManagedUserInput,
): Promise<ManagedUser> => {
  ensureSeed();
  const users = readAll();

  const normalizedUsername = input.username.trim();
  if (
    users.some(
      (u) => u.username.toLowerCase() === normalizedUsername.toLowerCase(),
    )
  ) {
    throw new Error("Username already exists");
  }

  const user: ManagedUser = {
    id: crypto?.randomUUID ? crypto.randomUUID() : String(Date.now()),
    ...input,
    username: normalizedUsername,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };

  writeAll([user, ...users]);
  return user;
};

export const updateManagedUser = async (
  id: string,
  input: ManagedUserInput,
): Promise<ManagedUser> => {
  ensureSeed();
  const users = readAll();
  const idx = users.findIndex((u) => u.id === id);
  if (idx < 0) throw new Error("User not found");

  const normalizedUsername = input.username.trim();
  if (
    users.some(
      (u) =>
        u.id !== id &&
        u.username.toLowerCase() === normalizedUsername.toLowerCase(),
    )
  ) {
    throw new Error("Username already exists");
  }

  const updated: ManagedUser = {
    ...users[idx],
    ...input,
    username: normalizedUsername,
    updatedAt: nowIso(),
  };

  const next = [...users];
  next[idx] = updated;
  writeAll(next);
  return updated;
};

export const deleteManagedUser = async (id: string): Promise<void> => {
  ensureSeed();
  const users = readAll();
  writeAll(users.filter((u) => u.id !== id));
};
